import { useEffect, useRef } from 'react';

// Binds a LiveKit video track to a <video> element this component owns, and
// unbinds it on unmount or whenever the track/visibility changes.
//
// The hook that produces tracks (useVoiceConnection) deliberately never
// attaches video itself: adaptiveStream only keeps a remote subscription alive
// while the track is attached to a real, sized, on-screen element, which only
// the component actually rendering it can provide.
//
// `enabled` has to be threaded through rather than inferred from `track`,
// because turning a camera off calls LiveKit's track.mute(), not unpublish -
// the track object stays put and simply stops producing frames, which renders
// as a frozen or black <video> instead of going away. Callers pass
// participant.cameraEnabled, which already accounts for that. It also means
// the element unmounts (the caller swaps in an avatar) only after this
// effect's detach() has run, instead of leaking the binding.
export function useAttachedTrack(track, enabled) {
    const elementRef = useRef(null);

    useEffect(() => {
        const element = elementRef.current;
        if (!element || !track || !enabled) return undefined;
        track.attach(element);
        return () => { track.detach(element); };
    }, [track, enabled]);

    return elementRef;
}
